/**
 * The AI consent acknowledgement rule the SPA and the façade must agree on (steward-app#2542).
 *
 * The SPA asks before it lets anyone send a question (`src/assistant/AssistantProvider.tsx`);
 * the façade refuses a question without the acknowledgement (`beginTurn` in
 * `services/steward-app-api/src/handlers/assistant.ts`). Both read it through
 * {@link hasAiConsent}, so the two cannot disagree about what counts. Before #2542 the rule and
 * its version numbers lived only in the SPA (`src/api/types.ts`), which still re-exports them.
 *
 * Unlike the credential vault agreement (`./credentialVault.ts`), the acknowledgement is written
 * by the SPA itself into the caller's own preferences blob (`PUT /me`). It records that the
 * person's own client showed them the notice and they accepted it; nothing here makes it
 * tamper-proof against that person.
 */

/** Current substance version of the AI consent notice (docs/15-ai-consent-privacy.md).
 *  Bump ONLY when the consented-to substance materially changes (new data categories, new
 *  processing, weakened guarantees) — users with an older version re-see the modal, and the
 *  façade refuses their questions until they accept. Wording tweaks and equivalent
 *  vendor/model swaps do not bump it. */
export const AI_CONSENT_VERSION = 1;

/** Current substance version of the FIRM variant of the AI consent notice (steward-app#2523).
 *  Versioned apart from {@link AI_CONSENT_VERSION} so a change to either dialog re-prompts only
 *  the people who saw that one. Same bump rule.
 *
 *  2 (2026-10-26, steward-app#1817): firm conversations are now saved, where version 1 told
 *  firm users they were not. A version 1 acknowledgment no longer counts for a saved
 *  conversation. (The façade's unsaved legacy firm path still accepts version 1; see
 *  `beginTurn`.) */
export const AI_FIRM_CONSENT_VERSION = 2;

/** Which consent dialog an acknowledgment is for: the household one or the firm one. */
export type AiConsentScope = 'family' | 'firm';

/** The code on the façade's 403 when a question arrives without the acknowledgement. The SPA
 *  branches on it to offer the notice again. */
export const AI_CONSENT_REQUIRED_CODE = 'AI_CONSENT_REQUIRED';

/** The preference keys each scope's acknowledgement is recorded under. */
export const AI_CONSENT_PREFERENCE_KEYS = {
  family: { at: 'aiConsentAcknowledgedAt', version: 'aiConsentVersion' },
  firm: { at: 'aiFirmConsentAcknowledgedAt', version: 'aiFirmConsentVersion' },
} as const satisfies Record<AiConsentScope, { at: string; version: string }>;

/** The version a scope's acknowledgement must reach today. */
export function currentAiConsentVersion(scope: AiConsentScope): number {
  return scope === 'firm' ? AI_FIRM_CONSENT_VERSION : AI_CONSENT_VERSION;
}

/**
 * Has this person acknowledged the given scope's AI consent notice, at `minimumVersion` or
 * later (the scope's current version unless said otherwise)?
 *
 * - The timestamp must be a non-empty string (docs/15 C3: `null` or absent is not acknowledged).
 * - The two scopes are independent (steward-app#2523): a family acknowledgement never satisfies
 *   the firm notice, nor the reverse.
 * - An absent or `null` FAMILY version reads as 1: acknowledgements predating the version field
 *   carried none. An absent FIRM version is not acknowledged: the firm pair was introduced
 *   with its version, so there is no older shape to honour.
 * - A present version must be a number. The SPA only ever writes the numeric constants above.
 */
export function hasAiConsent(
  preferences: Record<string, unknown> | null | undefined,
  scope: AiConsentScope,
  minimumVersion: number = currentAiConsentVersion(scope),
): boolean {
  if (!preferences) return false;
  const keys = AI_CONSENT_PREFERENCE_KEYS[scope];
  const at = preferences[keys.at];
  const version = preferences[keys.version] ?? (scope === 'family' ? 1 : undefined);
  return typeof at === 'string' && at.length > 0
    && typeof version === 'number' && version >= minimumVersion;
}
